/**
 * EventHighlightsWidget — Iter-313j
 *
 * Compact tile widget showing approved event photos. Rendered on the
 * Talent Home so newcomers see immediate social proof AND get a direct
 * "Share a highlight" CTA — even before anyone else has posted.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, ArrowRight, Camera, Plus } from 'lucide-react';
import api from '../utils/api';
import EventHighlightUploader from './EventHighlightUploader';

const EventHighlightsWidget = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    try {
      const res = await api.get('/event-highlights?limit=3');
      setItems(res.data?.items || []);
    } catch (e) {
      // Silent — dashboard should never fail because of this widget,
      // but log so devs can spot broken auth / 500s.
      console.warn('[EventHighlightsWidget] failed to load:', e?.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return null;

  // Empty state — CTA so talent learn the feature exists.
  if (items.length === 0) {
    return (
      <>
        <Card data-testid="event-highlights-widget-empty" className="border-dashed border-cyan-200 bg-cyan-50/40">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center shrink-0">
              <Camera className="w-5 h-5 text-cyan-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Share a moment from your last event</p>
              <p className="text-[11px] text-gray-600">Highlights are reviewed by our team before appearing on the Community Feed.</p>
            </div>
            <Button
              size="sm"
              className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 text-[11px]"
              onClick={() => setUploaderOpen(true)}
              data-testid="event-highlights-widget-add-empty"
            >
              <Plus className="w-3 h-3 mr-1" /> Share
            </Button>
          </CardContent>
        </Card>
        <EventHighlightUploader open={uploaderOpen} onOpenChange={setUploaderOpen} onUploaded={fetch} />
      </>
    );
  }

  return (
    <>
      <Card data-testid="event-highlights-widget" className="border-cyan-100">
        <CardHeader className="py-3 px-4 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-600" />
            On the Job
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="sm"
              className="text-cyan-700 hover:bg-cyan-50 h-7 text-[11px]"
              onClick={() => setUploaderOpen(true)}
              data-testid="event-highlights-widget-add"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
            <Button
              variant="ghost" size="sm"
              className="text-cyan-700 hover:bg-cyan-50 h-7 text-[11px]"
              onClick={() => navigate('/community')}
              data-testid="event-highlights-widget-see-all"
            >
              See all <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-3 pt-0 grid grid-cols-3 gap-2">
          {items.map((h) => (
            <button
              key={h.id}
              onClick={() => navigate('/community')}
              className="group relative aspect-square rounded-lg overflow-hidden bg-slate-100 hover:ring-2 hover:ring-cyan-300 transition"
              data-testid={`event-highlight-tile-${h.id}`}
            >
              <img
                src={h.photo_url}
                alt={h.event_title || 'Event highlight'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {(h.city || h.event_title) && (
                <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 bg-black/50 text-white text-[9px] font-medium truncate">
                  {h.event_title || `${h.city || ''}${h.state ? `, ${h.state}` : ''}`}
                </div>
              )}
            </button>
          ))}
        </CardContent>
      </Card>
      <EventHighlightUploader open={uploaderOpen} onOpenChange={setUploaderOpen} onUploaded={fetch} />
    </>
  );
};

export default EventHighlightsWidget;
